import { ArrowLeft, Clock, SearchMd } from '@timelyapp/icons/line-icons';
import { Plus } from '@timelyapp/icons/solid-icons';
import { Button, Dropdown, RadixTooltip, Spinner } from '@timelyapp/react';
import classNames from 'classnames';
import type React from 'react';
import {
  type ButtonHTMLAttributes,
  type CSSProperties,
  forwardRef,
  type ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { AgentProjectCreateForm, type ProjectCreation } from './AgentProjectCreateForm';
import styles from './AgentProjectPopover.module.scss';
import {
  ProjectPickerAcceptedContext,
  type ProjectPickerDetails,
  ProjectPickerDetailsContext,
} from './ProjectPickerDetailsContext';

export interface ProjectOption {
  id: number;
  name: string;
  client?: string;
  color?: string;
  group?: string;
  disabled?: boolean;
}
export interface ProjectPopoverProps {
  value: number | null;
  label: string;
  projectColor?: string;
  suggested?: boolean;
  labelSuffix?: string;
  revealOnHover?: boolean;
  ariaLabel: string;
  options: ProjectOption[];
  disabled?: boolean;
  loading?: boolean;
  /** `busy` swaps the symbol for a spinner; `fresh` briefly highlights the button. */
  indicator?: { symbol: string; label: string; busy?: boolean; fresh?: boolean };
  incomplete?: {
    count: number;
    total: number;
    periods?: {
      id: number;
      projectId: number | null;
      time: string;
      date?: string;
      pendingLabel?: string;
    }[];
    projectName?: string;
    complete?: () => Promise<void>;
  };
  predictions?: ReactNode;
  predictionsInSummary?: boolean;
  predictionBusy?: boolean;
  creation?: ProjectCreation;
  /** "button" renders the trigger as the shared Button, for toolbars; "pill" is the table chip. */
  appearance?: 'pill' | 'button';
  onChange: (id: number | null) => Promise<void>;
  onSearch?: (query: string) => void;
  /** Called as the picker opens, e.g. to load what its options need. */
  onOpen?: () => void;
}

const MIN_PROJECT_LIST_HEIGHT = 250;

/** Agent Tracking's own picker: ordinary project pickers elsewhere remain unchanged. */
export function AgentProjectPopover({
  value,
  label,
  projectColor,
  suggested,
  labelSuffix,
  revealOnHover,
  ariaLabel,
  options,
  disabled,
  loading,
  indicator,
  predictions,
  predictionsInSummary = false,
  predictionBusy = false,
  incomplete,
  creation,
  appearance = 'pill',
  onChange,
  onSearch,
  onOpen,
}: ProjectPopoverProps) {
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [details, setDetails] = useState<ProjectPickerDetails | null>(null);
  const [query, setQuery] = useState('');
  const [searchFocused, setSearchFocused] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const focusBack = useCallback((node: HTMLButtonElement | null) => node?.focus(), []);
  const focusSearch = useCallback((node: HTMLInputElement | null) => node?.focus(), []);
  const list = useRef<HTMLDivElement | null>(null);
  const [scrollBody, setScrollBody] = useState<HTMLDivElement | null>(null);
  const [scrollList, setScrollList] = useState<HTMLDivElement | null>(null);
  const attachList = useCallback((node: HTMLDivElement | null) => {
    list.current = node;
    setScrollList(node);
  }, []);
  const panel = useRef<HTMLDivElement>(null);
  const [moreBelow, setMoreBelow] = useState(false);
  const [projectListHeight, setProjectListHeight] = useState(MIN_PROJECT_LIST_HEIGHT);
  useLayoutEffect(() => {
    if (!open || creating || details) return;
    const scrollers = [scrollBody, scrollList].filter((node): node is HTMLDivElement => !!node);
    const update = () => {
      if (scrollList) {
        const origin = scrollList.getBoundingClientRect().top;
        const rows = Array.from(scrollList.querySelectorAll('button')).map(row => {
          const rect = row.getBoundingClientRect();
          return { top: rect.top - origin + scrollList.scrollTop, height: rect.height };
        });
        // Grow the list into the room the popup has left, never below the default height.
        const surface = panel.current;
        const popupMax = surface ? Number.parseFloat(getComputedStyle(surface).maxHeight) : NaN;
        const limit =
          surface && scrollBody && Number.isFinite(popupMax)
            ? Math.max(
                MIN_PROJECT_LIST_HEIGHT,
                Math.floor(
                  popupMax -
                    (surface.offsetHeight - scrollBody.clientHeight) -
                    (scrollBody.scrollHeight - scrollList.offsetHeight)
                )
              )
            : MIN_PROJECT_LIST_HEIGHT;
        // Clip through a row, rather than between rows, to reveal there is more to scroll.
        const edgeRow = rows.filter(row => row.height > 0 && row.top < limit).at(-1);
        setProjectListHeight(
          scrollList.scrollHeight > limit && edgeRow
            ? Math.ceil(edgeRow.top + edgeRow.height * 0.8)
            : limit
        );
      }
      setMoreBelow(
        scrollers.some(node => node.scrollHeight - node.clientHeight - node.scrollTop > 1)
      );
    };
    const resize = new ResizeObserver(update);
    const mutation = new MutationObserver(update);
    scrollers.forEach(node => {
      resize.observe(node);
      node.addEventListener('scroll', update, { passive: true });
    });
    window.addEventListener('resize', update);
    if (scrollBody)
      mutation.observe(scrollBody, { childList: true, subtree: true, characterData: true });
    update();
    return () => {
      resize.disconnect();
      mutation.disconnect();
      scrollers.forEach(node => node.removeEventListener('scroll', update));
      window.removeEventListener('resize', update);
    };
  }, [open, creating, details, scrollBody, scrollList]);
  const trigger = useRef<HTMLButtonElement>(null);
  const [predictionHeight, setPredictionHeight] = useState<number>();
  useLayoutEffect(() => {
    if (!open || !predictionBusy || predictionHeight !== undefined || creating || details) return;
    const height = panel.current?.getBoundingClientRect().height;
    if (height) setPredictionHeight(height);
  }, [open, predictionBusy, predictionHeight, creating, details]);
  const [recentlyActive, setRecentlyActive] = useState(false);
  const highlightTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => () => clearTimeout(highlightTimer.current), []);
  const clearHighlight = () => {
    clearTimeout(highlightTimer.current);
    setRecentlyActive(false);
  };
  const closeWithHighlight = () => {
    clearTimeout(highlightTimer.current);
    setRecentlyActive(true);
    highlightTimer.current = setTimeout(() => setRecentlyActive(false), 3000);
    setOpen(false);
    setCreating(false);
    setDetails(null);
    trigger.current?.focus();
  };
  const current = options.find(p => p.id === value);
  const liveTrigger = {
    label,
    suggested,
    labelSuffix,
    indicator,
    incomplete,
    color: projectColor ?? current?.color,
  };
  const [openedTrigger, setOpenedTrigger] = useState(liveTrigger);
  // Results update inside the picker immediately; its anchor updates after closing.
  const shownTrigger = open ? openedTrigger : liveTrigger;
  const partiallyAssigned =
    !!incomplete && incomplete.count > 0 && incomplete.count < incomplete.total;
  const shownPartiallyAssigned =
    !!shownTrigger.incomplete &&
    shownTrigger.incomplete.count > 0 &&
    shownTrigger.incomplete.count < shownTrigger.incomplete.total;
  const pendingPeriods =
    incomplete?.periods?.filter(p => p.projectId === null && p.pendingLabel) ?? [];
  const unassignedCount = (incomplete?.count ?? 0) - pendingPeriods.length;
  const shownNeedsAssignment =
    shownPartiallyAssigned &&
    (shownTrigger.incomplete!.periods?.filter(p => p.projectId === null && !p.pendingLabel)
      .length ?? shownTrigger.incomplete!.count) > 0;
  const orderedPeriods = [...(incomplete?.periods ?? [])].sort(
    (a, b) => Number(b.projectId === null) - Number(a.projectId === null)
  );
  const multipleDays = new Set(orderedPeriods.map(p => p.date)).size > 1;
  const filtered =
    !query && current
      ? [{ ...current, group: 'Selected' }, ...options.filter(p => p.id !== value)]
      : options.filter(p =>
          `${p.name} ${p.client ?? ''}`.toLocaleLowerCase().includes(query.toLocaleLowerCase())
        );
  const choose = async (projectId: number | null) => {
    if (saving) return;
    setSaving(true);
    setError('');
    try {
      await onChange(projectId);
      closeWithHighlight();
    } catch (e) {
      setError((e as Error).message || 'Could not update the project. Try again.');
    } finally {
      setSaving(false);
    }
  };
  return (
    <Dropdown
      align="center"
      side="right"
      sideOffset={12}
      showArrow={false}
      className={styles.pickerSurface}
      aria-label={ariaLabel}
      open={open}
      onEscapeKeyDown={event => {
        if (saving) {
          event.preventDefault();
          return;
        }
        if (creating) {
          event.preventDefault();
          setCreating(false);
          return;
        }
        if (!details) return;
        event.preventDefault();
        const restore = details.returnFocus;
        setDetails(null);
        requestAnimationFrame(restore);
      }}
      onOpenChange={next => {
        if (saving) return;
        if (!next) {
          closeWithHighlight();
          return;
        }
        setOpen(next);
        if (next) {
          onOpen?.();
          clearHighlight();
          setOpenedTrigger(liveTrigger);
          setQuery('');
          setPredictionHeight(undefined);
          setError('');
        }
      }}
      trigger={
        <ProjectPill
          ref={trigger}
          asButton={appearance === 'button'}
          isActive={open}
          data-agent-project-trigger
          data-recently-active={recentlyActive || undefined}
          data-suggested={shownTrigger.suggested || undefined}
          data-empty-assignment={revealOnHover || undefined}
          type="button"
          className={appearance === 'button' ? styles.triggerButton : styles.trigger}
          disabled={disabled}
          aria-label={ariaLabel}
          tooltip={
            shownNeedsAssignment
              ? `${shownTrigger.incomplete!.count} of ${shownTrigger.incomplete!.total} periods ${shownTrigger.incomplete!.count === 1 ? 'needs' : 'need'} a project.`
              : shownTrigger.suggested
                ? 'Suggested project · Click to review'
                : undefined
          }
          title={
            shownTrigger.suggested || shownPartiallyAssigned
              ? undefined
              : [
                  ...new Set([
                    shownTrigger.label,
                    shownTrigger.labelSuffix,
                    shownTrigger.indicator?.label,
                  ]),
                ]
                  .filter(Boolean)
                  .join(' · ')
          }
          data-attention={shownTrigger.indicator?.symbol === '!' || undefined}
          data-arrived={shownTrigger.indicator?.fresh || undefined}
        >
          {shownTrigger.suggested ? (
            <span className={styles.indicator} aria-hidden="true">
              ✧
            </span>
          ) : (
            <span
              className={styles.dot}
              style={
                {
                  '--project-color': shownTrigger.color
                    ? `#${shownTrigger.color.replace(/^#/, '')}`
                    : undefined,
                } as CSSProperties
              }
            />
          )}
          <span className={styles.triggerLabel}>{shownTrigger.label}</span>
          {shownTrigger.labelSuffix && (
            <span className={styles.triggerStatus}>{shownTrigger.labelSuffix}</span>
          )}
          {shownNeedsAssignment && (
            <span className={styles.incompleteBadge} aria-label="Incomplete project assignment">
              !
            </span>
          )}
          {shownTrigger.indicator &&
            !(shownTrigger.suggested && shownTrigger.indicator.symbol === '✧') && (
              <span
                className={styles.indicator}
                aria-hidden={shownTrigger.indicator.label === shownTrigger.label || undefined}
                aria-label={
                  shownTrigger.indicator.label === shownTrigger.label
                    ? undefined
                    : shownTrigger.indicator.label
                }
              >
                {shownTrigger.indicator.busy ? (
                  <span aria-hidden="true" className={styles.indicatorSpinner}>
                    <Spinner size={10} />
                  </span>
                ) : (
                  shownTrigger.indicator.symbol
                )}
              </span>
            )}
        </ProjectPill>
      }
    >
      {open && (
        <ProjectPickerDetailsContext.Provider value={setDetails}>
          <div
            ref={panel}
            style={
              {
                '--prediction-height': predictionHeight ? `${predictionHeight}px` : undefined,
              } as CSSProperties
            }
            data-more-below={(moreBelow && !creating && !details) || undefined}
            data-project-picker-panel
            data-stable-layout={(!creating && !details) || undefined}
            className={styles.popup}
            aria-busy={saving}
          >
            {details && (
              <div className={styles.inlineExplanation}>
                <header className={styles.heading}>
                  <div>
                    <button
                      type="button"
                      className={styles.backButton}
                      ref={focusBack}
                      onClick={() => {
                        const restore = details.returnFocus;
                        setDetails(null);
                        requestAnimationFrame(restore);
                      }}
                    >
                      <ArrowLeft width={16} height={16} /> Back to projects
                    </button>
                    <strong>Why this project?</strong>
                    {details.projectLabel ?? <span>{details.projectName}</span>}
                  </div>
                </header>
                <div className={styles.evidenceBody}>{details.content}</div>
              </div>
            )}
            <div className={styles.pickerContents} hidden={!!details}>
              <div className={styles.body} ref={setScrollBody}>
                {creating && creation ? (
                  <AgentProjectCreateForm
                    initialName={query}
                    creation={creation}
                    onBack={() => setCreating(false)}
                    onBusyChange={setSaving}
                    onAssign={async id => {
                      await onChange(id);
                      closeWithHighlight();
                    }}
                    onFull={(name, client, color) => {
                      setOpen(false);
                      setCreating(false);
                      requestAnimationFrame(() => creation.openFull(name, client, color));
                    }}
                  />
                ) : (
                  <>
                    <div className={incomplete ? styles.assignmentWorkflow : undefined}>
                      {incomplete && (
                        <div className={styles.incompleteNotice}>
                          <strong>
                            {pendingPeriods.length > 0
                              ? `${incomplete.total - incomplete.count} ${incomplete.total - incomplete.count === 1 ? 'period' : 'periods'} assigned · ${pendingPeriods.length} pending${unassignedCount ? ` · ${unassignedCount} unassigned` : ''}`
                              : `${incomplete.total - incomplete.count} of ${incomplete.total} periods assigned`}
                          </strong>
                          {[
                            ...new Set(
                              incomplete.periods?.flatMap(p =>
                                p.projectId === null ? [] : [p.projectId]
                              )
                            ),
                          ].map(id => {
                            const assignedProject = options.find(p => p.id === id);
                            const count = incomplete.periods!.filter(
                              p => p.projectId === id
                            ).length;
                            return (
                              <div className={styles.assignmentSummaryRow} key={id}>
                                <span
                                  className={styles.dot}
                                  aria-hidden="true"
                                  style={
                                    {
                                      '--project-color': assignedProject?.color
                                        ? `#${assignedProject.color.replace(/^#/, '')}`
                                        : undefined,
                                    } as CSSProperties
                                  }
                                />
                                <span>
                                  <strong>{assignedProject?.name ?? 'Assigned project'}</strong>
                                  {assignedProject?.client && (
                                    <span className={styles.muted}>
                                      {' '}
                                      ({assignedProject.client})
                                    </span>
                                  )}
                                </span>
                                <span className={styles.assignmentCount}>
                                  {count} {count === 1 ? 'period' : 'periods'}
                                </span>
                              </div>
                            );
                          })}
                          {unassignedCount > 0 && (
                            <div
                              className={classNames(styles.assignmentSummaryRow, {
                                [styles.unassignedSummary]: partiallyAssigned,
                              })}
                            >
                              <span
                                className={partiallyAssigned ? styles.incompleteBadge : styles.dot}
                                aria-hidden="true"
                              >
                                {partiallyAssigned ? '!' : null}
                              </span>
                              <span>
                                Unassigned
                                {unassignedCount === 1 && (
                                  <span className={styles.muted}>
                                    {' '}
                                    ·{' '}
                                    {
                                      incomplete.periods?.find(
                                        p => p.projectId === null && !p.pendingLabel
                                      )?.time
                                    }
                                  </span>
                                )}
                              </span>
                              <span className={styles.assignmentCount}>
                                {unassignedCount} {unassignedCount === 1 ? 'period' : 'periods'}
                              </span>
                            </div>
                          )}
                          {[...new Set(pendingPeriods.map(p => p.pendingLabel))].map(status => {
                            const group = pendingPeriods.filter(p => p.pendingLabel === status);
                            return group.length > 0 ? (
                              <div key={status} className={styles.assignmentSummaryRow}>
                                {status === 'Waiting for summary' ? (
                                  <Clock width={14} height={14} aria-hidden="true" />
                                ) : (
                                  <Spinner size={12} />
                                )}
                                <span>
                                  {status}
                                  {group.length === 1 && (
                                    <span className={styles.muted}> · {group[0].time}</span>
                                  )}
                                </span>
                                <span className={styles.assignmentCount}>
                                  {group.length} {group.length === 1 ? 'period' : 'periods'}
                                </span>
                              </div>
                            ) : null;
                          })}
                          {predictionsInSummary && (
                            <ProjectPickerAcceptedContext.Provider value={closeWithHighlight}>
                              {predictions}
                            </ProjectPickerAcceptedContext.Provider>
                          )}
                          {!!incomplete.periods?.length && (
                            <details className={styles.assignmentPeriods}>
                              <summary>
                                <span>View periods</span>
                                {partiallyAssigned && (
                                  <span className={styles.muted}> · Unassigned first</span>
                                )}
                              </summary>
                              <ul>
                                {orderedPeriods.map(period => {
                                  const assignedProject = options.find(
                                    p => p.id === period.projectId
                                  );
                                  return (
                                    <li
                                      key={period.id}
                                      data-unassigned={
                                        (partiallyAssigned &&
                                          period.projectId === null &&
                                          !period.pendingLabel) ||
                                        undefined
                                      }
                                    >
                                      <span>
                                        {multipleDays && period.date ? `${period.date} · ` : ''}
                                        {period.time}
                                      </span>
                                      <span className={styles.periodProject}>
                                        {period.projectId === null && period.pendingLabel ? (
                                          <Clock width={14} height={14} aria-hidden="true" />
                                        ) : period.projectId === null && partiallyAssigned ? (
                                          <span
                                            className={styles.incompleteBadge}
                                            aria-hidden="true"
                                          >
                                            !
                                          </span>
                                        ) : (
                                          <span
                                            className={styles.dot}
                                            aria-hidden="true"
                                            style={
                                              {
                                                '--project-color': assignedProject?.color
                                                  ? `#${assignedProject.color.replace(/^#/, '')}`
                                                  : undefined,
                                              } as CSSProperties
                                            }
                                          />
                                        )}
                                        {period.projectId === null
                                          ? (period.pendingLabel ?? 'Unassigned')
                                          : (assignedProject?.name ?? 'Assigned project')}
                                      </span>
                                    </li>
                                  );
                                })}
                              </ul>
                            </details>
                          )}
                        </div>
                      )}
                      {!predictionsInSummary && incomplete?.complete && incomplete.projectName && (
                        <button
                          type="button"
                          className={styles.completeAssignment}
                          aria-label={`Assign remaining to ${incomplete.projectName}`}
                          disabled={saving}
                          onClick={async () => {
                            setSaving(true);
                            setError('');
                            try {
                              await incomplete.complete?.();
                              closeWithHighlight();
                            } catch (e) {
                              setError(
                                (e as Error).message ||
                                  'Could not assign the remaining periods. Try again.'
                              );
                            } finally {
                              setSaving(false);
                            }
                          }}
                        >
                          Assign remaining
                        </button>
                      )}
                      {!predictionsInSummary && (
                        <ProjectPickerAcceptedContext.Provider value={closeWithHighlight}>
                          {predictions}
                        </ProjectPickerAcceptedContext.Provider>
                      )}
                    </div>
                    <section className={styles.chooser} aria-label="Choose a project">
                      {incomplete && (
                        <div className={styles.assignmentScope}>
                          Change project for the whole session
                        </div>
                      )}
                      <div className={styles.searchRow}>
                        <label className={styles.search}>
                          <SearchMd width={16} height={16} aria-hidden="true" />
                          <input
                            ref={focusSearch}
                            aria-label="Search projects"
                            placeholder="Search projects or clients…"
                            value={query}
                            onFocus={() => setSearchFocused(true)}
                            onBlur={() => setSearchFocused(false)}
                            onChange={event => {
                              setQuery(event.target.value);
                              onSearch?.(event.target.value);
                            }}
                            onKeyDown={event => {
                              if (event.nativeEvent.isComposing) return;
                              if (event.key === 'Enter' && query.trim()) {
                                event.preventDefault();
                                list.current
                                  ?.querySelector<HTMLButtonElement>('button:not(:disabled)')
                                  ?.click();
                              }
                              if (event.key === 'ArrowDown') {
                                event.preventDefault();
                                list.current
                                  ?.querySelector<HTMLButtonElement>('button:not(:disabled)')
                                  ?.focus();
                              }
                            }}
                          />
                        </label>
                        {creation && (
                          <RadixTooltip tooltip="Create new project" side="top" delayDuration={0}>
                            <button
                              type="button"
                              className={styles.createButton}
                              aria-label="Create new project"
                              disabled={saving}
                              onClick={() => setCreating(true)}
                            >
                              <Plus width={20} height={20} aria-hidden="true" />
                            </button>
                          </RadixTooltip>
                        )}
                      </div>
                      <div
                        className={styles.projects}
                        style={{ maxHeight: projectListHeight }}
                        ref={attachList}
                        onKeyDown={event => {
                          if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
                          const buttons = Array.from(
                            list.current?.querySelectorAll<HTMLButtonElement>(
                              'button:not(:disabled)'
                            ) ?? []
                          );
                          const index = buttons.indexOf(
                            document.activeElement as HTMLButtonElement
                          );
                          if (index < 0 || !buttons.length) return;
                          event.preventDefault();
                          buttons[
                            event.key === 'Home'
                              ? 0
                              : event.key === 'End'
                                ? buttons.length - 1
                                : (index + (event.key === 'ArrowDown' ? 1 : -1) + buttons.length) %
                                  buttons.length
                          ]?.focus();
                        }}
                      >
                        {filtered.map((project, index) => (
                          <div key={project.id}>
                            {!query &&
                              (index === 0 || filtered[index - 1].group !== project.group) && (
                                <div className={styles.sectionLabel}>
                                  {project.group ?? 'Projects'}
                                </div>
                              )}
                            <button
                              type="button"
                              disabled={saving || project.disabled}
                              aria-pressed={value === project.id}
                              className={styles.option}
                              data-keyboard-active={
                                (searchFocused &&
                                  !!query.trim() &&
                                  filtered.find(p => !p.disabled)?.id === project.id) ||
                                undefined
                              }
                              onClick={() => void choose(project.id)}
                            >
                              <span
                                className={styles.dot}
                                style={
                                  {
                                    '--project-color': project.color
                                      ? `#${project.color.replace(/^#/, '')}`
                                      : undefined,
                                  } as CSSProperties
                                }
                              />
                              <span className={styles.optionLabel}>
                                <strong>{project.name}</strong>
                                {project.client && (
                                  <>
                                    {' '}
                                    <small>({project.client})</small>
                                  </>
                                )}
                              </span>
                              {value === project.id && (
                                <span className={styles.check} aria-hidden="true">
                                  ✓
                                </span>
                              )}
                            </button>
                          </div>
                        ))}
                        {!filtered.length && !creation && (
                          <p className={styles.empty}>
                            {loading ? 'Loading projects…' : 'No matching projects'}
                          </p>
                        )}
                        {creation && (
                          <button
                            type="button"
                            className={`${styles.option} ${styles.createTrigger}`}
                            data-keyboard-active={
                              (searchFocused &&
                                !!query.trim() &&
                                !filtered.some(p => !p.disabled)) ||
                              undefined
                            }
                            onClick={() => setCreating(true)}
                          >
                            <span>
                              + {query.trim() ? `Create “${query.trim()}”` : 'Create new project'}
                            </span>
                            {searchFocused &&
                              !!query.trim() &&
                              !filtered.some(p => !p.disabled) && (
                                <span className={styles.enterHint} aria-hidden="true">
                                  Enter ↵
                                </span>
                              )}
                          </button>
                        )}
                      </div>
                    </section>
                  </>
                )}
              </div>
              {error && (
                <p role="alert" className={styles.error}>
                  {error}
                </p>
              )}
            </div>
          </div>
        </ProjectPickerDetailsContext.Provider>
      )}
    </Dropdown>
  );
}

// Preserve Radix's trigger props/ref through the optional tooltip.
const ProjectPill = forwardRef<
  HTMLButtonElement,
  ButtonHTMLAttributes<HTMLButtonElement> & {
    tooltip?: string;
    asButton?: boolean;
    isActive?: boolean;
  }
>(({ tooltip, asButton = false, isActive = false, ...props }, ref) => {
  const restoringFocus = useRef(false);
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const handlers = {
    onClick: (event: React.MouseEvent<HTMLButtonElement>) => {
      restoringFocus.current = true;
      props.onClick?.(event);
    },
    onFocus: (event: React.FocusEvent<HTMLButtonElement>) => {
      props.onFocus?.(event);
      if (restoringFocus.current) {
        event.preventDefault();
        restoringFocus.current = false;
      }
    },
  };
  const button = asButton ? (
    <Button
      size="small"
      priority="medium"
      caret
      isActive={isActive}
      {...props}
      ref={ref}
      {...handlers}
    />
  ) : (
    <button {...props} ref={ref} {...handlers} />
  );
  return (
    <RadixTooltip
      tooltip={tooltip}
      side="top"
      delayDuration={0}
      open={!!tooltip && tooltipOpen}
      onOpenChange={setTooltipOpen}
    >
      {button}
    </RadixTooltip>
  );
});
ProjectPill.displayName = 'ProjectPill';
